import { FC, useCallback, useMemo, useState } from 'react';
import Page, { PageProps } from '@/components/page';
import PageHeader from '@/components/page/header';
import { getAllConnectors } from '@/lib/services/connector.service';
import { Box } from '@mui/material';
import ConnectorIcon from '@/components/connector-icon';
import Button from '@/components/button';
import Tooltip from '@/components/tooltip';
import {
  ConnectorFeature,
  ConnectorFeatureLabels,
  ConnectorType,
  getConnectorOptions,
  getTypedConnector,
} from '@/lib/models/connector';
import { QueryKey } from '@/lib/query-client';
import useFeature from '@/hooks/use-feature.hook';
import PageFeatureToast from '@/components/page-feature-toast';
import { GridCard, GridCardCategory } from '@/components/card-grid/helpers';
import { useQuery } from 'react-query';
import CardGrid from '@/components/card-grid';
import StatusChip from '@/components/chip/status-chip';
import { sortBy, uniqBy } from 'lodash';
import { SortButton } from '@/components/button-bar/helpers';
import useQueryHelper from '@/hooks/use-query-helper';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';
import useDrawer from '@/hooks/use-drawer.hook';

const SortButtons: SortButton[] = [
  {
    sortType: 'alpha',
    icon: 'alpha-sort',
    tooltip: 'Sort alphabetically by name',
  },
  {
    sortType: 'category',
    icon: 'sort',
    tooltip: 'Sort by category',
  },
];

const categoryEnabled = (category: string) =>
  ['privateModels', 'contextData', 'ingress'].includes(category);

const ConnectorsPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const [availableSort, setAvailableSort] = useState<string>('category');
  const [enabledSort, setEnabledSort] = useState<string>('alpha');

  const {
    canChangeConnector,
    getTooltip,
    loading: featureLoading,
    canViewDemoConnectors,
  } = useFeature();

  const connectorsQuery = useQuery([QueryKey.ConnectorsList], async () => getAllConnectors());
  const { data: connectors, refetch } = connectorsQuery;
  const { showLoader: connectorsLoading } = useQueryHelper(connectorsQuery);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleView = useCallback(
    (connectorId?: string) => {
      openDrawer('connector', {
        id: connectorId || null,
      });
    },
    [openDrawer]
  );

  const handleAdd = useCallback(
    (connectorType: ConnectorType, typeLabel: string) => {
      openDrawer('connector', {
        id: null,
        connectorType,
        name: `${typeLabel} Connector`,
      });
    },
    [openDrawer]
  );

  const handleSortAvailable = (sortType: string) => {
    setAvailableSort(sortType);
  };

  const handleSortEnabled = (sortType: string) => {
    setEnabledSort(sortType);
  };

  const [enabledCards, enabledCategories]: [GridCard[], GridCardCategory[]] = useMemo(() => {
    const categories: GridCardCategory[] = [];
    let cards: GridCard[] = [];
    const hasCategories = enabledSort === 'category';

    const typedConnectors = (connectors || [])
      .map(getTypedConnector)
      .filter(({ demo }) => !demo || canViewDemoConnectors);

    typedConnectors.forEach((connector) => {
      const { id, name, type, typeLabel, status, supportedFeatures, deprecated } = connector;

      const activeFeatureIds = Object.keys(supportedFeatures)
        .filter((featureKey) => {
          const featureId = featureKey as ConnectorFeature;
          return categoryEnabled(featureId) && supportedFeatures[featureId];
        })
        .map((featureKey) => featureKey as ConnectorFeature);

      activeFeatureIds.forEach((featureId) => {
        if (categories.find(({ id }) => id === featureId)) {
          return;
        }

        categories.push({
          title: ConnectorFeatureLabels[featureId],
          id: featureId,
        });
      });

      const card = {
        id: `${id}-${type}`,
        IconEl: <ConnectorIcon connectorType={type} size="large" />,
        children: <StatusChip value={deprecated ? 'deprecated' : status} size="x-small" />,
        title: name,
        detail: typeLabel,
        onClick: () => handleView(id),
      };

      if (hasCategories) {
        activeFeatureIds.forEach((featureId) => {
          cards.push({
            ...card,
            categoryId: featureId,
            id: `${card.id}-${featureId}`,
          });
        });
        return;
      }

      cards.push(card);
    });

    cards = sortBy(cards, ['categoryId', 'typeLabel']);

    return [cards, hasCategories ? categories : []];
  }, [enabledSort, connectors, canViewDemoConnectors, handleView]);

  const [availableCards, availableCategories]: [GridCard[], GridCardCategory[]] = useMemo(() => {
    const categories: GridCardCategory[] = [];
    let cards: (GridCard & { connectorType: ConnectorType })[] = [];

    const connectorOpts = getConnectorOptions().filter(
      ({ meta }) => !meta?.deprecated && (!meta?.demo || canViewDemoConnectors)
    );

    Object.keys(ConnectorFeatureLabels).forEach((category) => {
      const featureId = category as ConnectorFeature;

      if (!categoryEnabled(category)) {
        return;
      }

      categories.push({
        title: ConnectorFeatureLabels[featureId],
        id: category,
      });

      connectorOpts
        .filter(({ meta }) => meta?.[featureId])
        .forEach(({ label, value, disabled }) => {
          const connectorType = value as ConnectorType;
          const title = label as string;

          cards.push({
            id: `${category}-${connectorType}`,
            IconEl: <ConnectorIcon connectorType={connectorType} size="large" />,
            title,
            detail: disabled ? 'Coming Soon' : '',
            onClick: disabled ? undefined : () => handleAdd(connectorType, title),
            categoryId: category,
            connectorType,
          });
        });
    });

    cards = sortBy(cards, ['title']);

    if (availableSort !== 'category') {
      cards = uniqBy(cards, 'connectorType');
      return [cards, []];
    }

    return [cards, categories];
  }, [availableSort, canViewDemoConnectors, handleAdd]);

  if (connectorsLoading || featureLoading) {
    return null;
  }

  return (
    <Page title="Connectors">
      <PageHeader>
        <PageBlurb>Manage connections to third-party services and networks.</PageBlurb>
        <PageTools>
          <Tooltip title={getTooltip('add-connector')} disabled={canChangeConnector}>
            <Button
              label="Add Connector"
              icon="plus"
              onClick={() => handleView()}
              disabled={!canChangeConnector}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <PageFeatureToast featureId="change-connector" can={canChangeConnector} />
      <CardGrid
        title="Enabled Connectors"
        cards={enabledCards}
        categories={enabledCategories}
        sortType={enabledSort}
        sortButtons={SortButtons}
        onSort={handleSortEnabled}
        showAdd={false}
        showEmpty
        onRefresh={handleRefresh}
      />

      <Box mt={4}>
        <CardGrid
          title="Available Connectors"
          cards={availableCards}
          categories={availableCategories}
          sortType={availableSort}
          sortButtons={SortButtons}
          onSort={handleSortAvailable}
          showAdd={false}
          showEmpty
        />
      </Box>
    </Page>
  );
};

export default ConnectorsPage;
